'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { BuilderProject, generatePageId, PublishedVersion, CustomDomain, BuilderPage, BuilderElement, ElementType } from '@/components/builder/builderTypes';
import { exportProjectJson, importProjectJson, generatePageHtml, generateSitemapXml, validateProject } from '@/components/builder/domGenerator';

// ─── Storage helpers ──────────────────────────────────────────────────────────
function loadProjects(): BuilderProject[] {
  try {
    const saved = localStorage.getItem('builder_projects');
    if (saved) return JSON.parse(saved);
  } catch {/* ignore */}
  return [];
}

function saveProjects(projects: BuilderProject[]) {
  try {
    localStorage.setItem('builder_projects', JSON.stringify(projects));
  } catch {/* ignore */}
}

function generateProjectId() {
  return `proj-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

function formatDate(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

const THUMBNAIL_GRADIENTS = [
'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'];


// ─── Publish Modal (Dashboard) ────────────────────────────────────────────────
function DashboardPublishModal({ project, onClose, onPublished



}: {project: BuilderProject;onClose: () => void;onPublished: (updated: BuilderProject) => void;}) {
  const [step, setStep] = useState<'validate' | 'generating' | 'done' | 'error'>('validate');
  const validation = validateProject(project);

  const handlePublish = () => {
    if (!validation.valid) return;
    setStep('generating');
    setTimeout(() => {
      try {
        const pages: Record<string, string> = {};
        for (const page of project.pages) {
          pages[page.id] = generatePageHtml(page, project);
        }
        const sitemap = generateSitemapXml(project);
        const version: PublishedVersion = {
          id: `v-${Date.now()}`,
          version: Date.now(),
          publishedAt: Date.now(),
          html: pages[project.pages.find((p) => p.isHomepage)?.id || project.pages[0]?.id] || '',
          pages,
          sitemapXml: sitemap
        };
        const updated: BuilderProject = {
          ...project,
          published: true,
          publishedVersion: version,
          publishHistory: [...(project.publishHistory || []).slice(-9), version],
          updatedAt: Date.now()
        };
        onPublished(updated);
        setStep('done');
      } catch {setStep('error');}
    }, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="rounded-2xl shadow-2xl w-full max-w-lg mx-4" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8' }}>
        <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: '#E8DFC8' }}>
          <h2 className="font-bold text-lg" style={{ color: '#2D2010' }}>
            {step === 'done' ? '🎉 Published!' : step === 'generating' ? '⚙️ Publishing...' : '🚀 Publish Website'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: '#9B8A60' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </button>
        </div>
        <div className="p-6 space-y-4">
          {step === 'validate' &&
          <>
              {!validation.valid &&
            <div className="rounded-xl p-4" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
                  <p className="text-sm font-semibold mb-2" style={{ color: '#DC2626' }}>❌ Errors (must fix)</p>
                  {validation.errors.map((e, i) => <p key={i} className="text-xs" style={{ color: '#DC2626' }}>• {e}</p>)}
                </div>
            }
              {validation.warnings.length > 0 &&
            <div className="rounded-xl p-4" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                  <p className="text-sm font-semibold mb-2" style={{ color: '#D97706' }}>⚠️ Warnings</p>
                  {validation.warnings.map((w, i) => <p key={i} className="text-xs" style={{ color: '#92400E' }}>• {w}</p>)}
                </div>
            }
              {validation.valid && validation.warnings.length === 0 &&
            <div className="rounded-xl p-4" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                  <p className="text-sm font-semibold" style={{ color: '#16A34A' }}>✅ Ready to publish!</p>
                </div>
            }
              <div className="rounded-xl p-4" style={{ background: '#F8F5EE', border: '1px solid #E8DFC8' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: '#5C4A1E' }}>Publishing: {project.name}</p>
                <p className="text-xs" style={{ color: '#9B8A60' }}>{project.pages.length} pages · HTML/CSS/JS + Sitemap</p>
                {project.publishedVersion &&
              <p className="text-xs mt-1" style={{ color: '#9B8A60' }}>
                    Previous version: {new Date(project.publishedVersion.publishedAt).toLocaleString()}
                  </p>
              }
              </div>
            </>
          }
          {step === 'generating' &&
          <div className="flex flex-col items-center py-6 gap-3">
              <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <p className="text-sm font-semibold" style={{ color: '#2D2010' }}>Generating production output...</p>
            </div>
          }
          {step === 'done' &&
          <div className="rounded-xl p-4" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
              <p className="text-sm font-semibold" style={{ color: '#16A34A' }}>✅ Website published successfully!</p>
              <p className="text-xs mt-1" style={{ color: '#166534' }}>{project.pages.length} pages generated with HTML/CSS/JS</p>
            </div>
          }
          {step === 'error' &&
          <div className="rounded-xl p-4" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
              <p className="text-sm font-semibold" style={{ color: '#DC2626' }}>❌ Publishing failed. Please try again.</p>
            </div>
          }
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#F2EDE3', color: '#5C4A1E', border: '1px solid #E8DFC8' }}>
            {step === 'done' ? 'Close' : 'Cancel'}
          </button>
          {step === 'validate' &&
          <button onClick={handlePublish} disabled={!validation.valid} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ background: validation.valid ? 'linear-gradient(135deg, #16a34a, #15803d)' : '#9CA3AF', color: '#fff', opacity: validation.valid ? 1 : 0.6 }}>
              🚀 Publish Now
            </button>
          }
          {step === 'error' &&
          <button onClick={() => setStep('validate')} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ background: '#C9A84C', color: '#1A1208' }}>Try Again</button>
          }
        </div>
      </div>
    </div>);

}

// ─── Custom Domain Modal ──────────────────────────────────────────────────────
function CustomDomainModal({ project, onClose, onSave



}: {project: BuilderProject;onClose: () => void;onSave: (domain: CustomDomain) => void;}) {
  const [domain, setDomain] = useState(project.customDomain?.domain || '');
  const [status] = useState<CustomDomain['status']>(project.customDomain?.status || 'pending');
  const token = project.customDomain?.verificationToken || `verify-${project.id.slice(0, 8)}`;

  const handleSave = () => {
    if (!domain.trim()) return;
    onSave({ domain: domain.trim(), status: 'pending', verificationToken: token, addedAt: Date.now() });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="rounded-2xl shadow-2xl w-full max-w-lg mx-4" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8' }}>
        <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: '#E8DFC8' }}>
          <div>
            <h2 className="font-bold text-lg" style={{ color: '#2D2010' }}>Custom Domain</h2>
            <p className="text-xs mt-0.5" style={{ color: '#9B8A60' }}>Connect your own domain to this website</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: '#9B8A60' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#5C4A1E' }}>Domain Name</label>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              className="w-full px-4 py-3 rounded-xl text-sm"
              style={{ background: '#FAF7F2', border: '1px solid #E8DFC8', color: '#2D2010', outline: 'none' }} />
            
          </div>
          {project.customDomain &&
          <div className="rounded-xl p-4" style={{ background: '#F8F5EE', border: '1px solid #E8DFC8' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: '#5C4A1E' }}>DNS Verification</p>
              <p className="text-xs mb-2" style={{ color: '#9B8A60' }}>Add this TXT record to your DNS settings:</p>
              <div className="rounded-lg p-2 font-mono text-xs" style={{ background: '#1A1208', color: '#C9A84C' }}>
                TXT @ {token}
              </div>
              <p className="text-xs mt-2" style={{ color: '#9B8A60' }}>Status: <span style={{ color: status === 'active' ? '#16A34A' : status === 'error' ? '#DC2626' : '#D97706', fontWeight: 600 }}>{status}</span></p>
            </div>
          }
          <div className="rounded-xl p-4" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
            <p className="text-xs font-semibold mb-1" style={{ color: '#1D4ED8' }}>ℹ️ How it works</p>
            <div className="space-y-1">
              {['Enter your domain name', 'Add the DNS TXT record for verification', 'Point your domain\'s A record to our servers', 'Domain goes live within 24-48 hours'].map((step, i) =>
              <p key={i} className="text-xs" style={{ color: '#1E40AF' }}>{i + 1}. {step}</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#F2EDE3', color: '#5C4A1E', border: '1px solid #E8DFC8' }}>Cancel</button>
          <button onClick={handleSave} disabled={!domain.trim()} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ background: '#C9A84C', color: '#1A1208', opacity: domain.trim() ? 1 : 0.5 }}>
            Save Domain
          </button>
        </div>
      </div>
    </div>);

}

// ─── Version History Modal ────────────────────────────────────────────────────
function VersionHistoryModal({ project, onClose, onRestore



}: {project: BuilderProject;onClose: () => void;onRestore: (version: PublishedVersion) => void;}) {
  const history = project.publishHistory || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="rounded-2xl shadow-2xl w-full max-w-lg mx-4" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8' }}>
        <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: '#E8DFC8' }}>
          <div>
            <h2 className="font-bold text-lg" style={{ color: '#2D2010' }}>Version History</h2>
            <p className="text-xs mt-0.5" style={{ color: '#9B8A60' }}>{history.length} published version{history.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: '#9B8A60' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </button>
        </div>
        <div className="p-6 max-h-96 overflow-y-auto">
          {history.length === 0 ?
          <div className="text-center py-8">
              <p className="text-sm" style={{ color: '#9B8A60' }}>No published versions yet</p>
            </div> :

          <div className="space-y-3">
              {[...history].reverse().map((v, i) =>
            <div key={v.id} className="rounded-xl p-4 border" style={{ borderColor: '#E8DFC8', background: i === 0 ? 'rgba(22,163,74,0.04)' : '#fff' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold" style={{ color: '#2D2010' }}>
                          {i === 0 ? '🟢 Current' : `Version ${history.length - i}`}
                        </p>
                        {i === 0 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#F0FDF4', color: '#16A34A' }}>Live</span>}
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: '#9B8A60' }}>
                        {new Date(v.publishedAt).toLocaleString()} · {Object.keys(v.pages).length} pages
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                    onClick={() => {
                      const html = v.html;
                      const blob = new Blob([html], { type: 'text/html' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `version-${v.id}.html`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="text-xs px-2 py-1 rounded-lg"
                    style={{ background: '#F2EDE3', color: '#5C4A1E', border: '1px solid #E8DFC8' }}>
                    
                        ↓ Download
                      </button>
                      {i !== 0 &&
                  <button
                    onClick={() => {onRestore(v);onClose();}}
                    className="text-xs px-2 py-1 rounded-lg"
                    style={{ background: '#C9A84C', color: '#1A1208' }}>
                    
                          Restore
                        </button>
                  }
                    </div>
                  </div>
                </div>
            )}
            </div>
          }
        </div>
        <div className="px-6 pb-6">
          <button onClick={onClose} className="w-full py-2.5 rounded-xl text-sm font-medium" style={{ background: '#F2EDE3', color: '#5C4A1E', border: '1px solid #E8DFC8' }}>Close</button>
        </div>
      </div>
    </div>);

}

// ─── Project Card ─────────────────────────────────────────────────────────────
interface ProjectCardProps {
  project: BuilderProject;
  index: number;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onPreview: () => void;
  onExport: () => void;
  onDomain: () => void;
  onVersions: () => void;
}

function ProjectCard({ project, index, onEdit, onDuplicate, onDelete, onPublish, onUnpublish, onPreview, onExport, onDomain, onVersions }: ProjectCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const gradient = THUMBNAIL_GRADIENTS[index % THUMBNAIL_GRADIENTS.length];

  return (
    <div
      className="rounded-2xl border overflow-hidden transition-all group"
      style={{ background: '#FFFFFF', borderColor: '#E8DFC8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
      onMouseEnter={(e) => {(e.currentTarget as HTMLElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.12)';(e.currentTarget as HTMLElement).style.borderColor = '#C9A84C';}}
      onMouseLeave={(e) => {(e.currentTarget as HTMLElement).style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';(e.currentTarget as HTMLElement).style.borderColor = '#E8DFC8';}}>
      
      {/* Thumbnail */}
      <div className="relative overflow-hidden" style={{ height: 160, background: gradient }}>
        <div className="absolute inset-0 flex flex-col" style={{ padding: '12px' }}>
          <div className="rounded flex items-center gap-2 px-2 py-1.5 mb-2" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}>
            <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(255,255,255,0.6)' }} />
            <div className="flex-1 h-1.5 rounded" style={{ background: 'rgba(255,255,255,0.4)' }} />
            <div className="w-8 h-1.5 rounded" style={{ background: 'rgba(255,255,255,0.4)' }} />
          </div>
          <div className="flex-1 rounded" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <div className="p-2 space-y-1.5">
              <div className="h-3 rounded w-3/4" style={{ background: 'rgba(255,255,255,0.5)' }} />
              <div className="h-2 rounded w-full" style={{ background: 'rgba(255,255,255,0.3)' }} />
              <div className="h-2 rounded w-5/6" style={{ background: 'rgba(255,255,255,0.3)' }} />
              <div className="h-6 rounded w-24 mt-2" style={{ background: 'rgba(255,255,255,0.4)' }} />
            </div>
          </div>
        </div>
        {/* Hover overlay */}
        <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <button onClick={onEdit} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: '#C9A84C', color: '#1A1208' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            Edit
          </button>
          <button onClick={onPreview} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: 'rgba(255,255,255,0.9)', color: '#1A1208' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            Preview
          </button>
        </div>
        {/* Status badge */}
        <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: project.published ? 'rgba(22,163,74,0.9)' : 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '10px' }}>
            {project.published ? '● Live' : '○ Draft'}
          </span>
          {project.customDomain &&
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(37,99,235,0.8)', color: '#fff', fontSize: '10px' }}>
              🌐 {project.customDomain.domain}
            </span>
          }
        </div>
      </div>

      {/* Info */}
      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm truncate" style={{ color: '#2D2010' }}>{project.name}</h3>
            <p className="text-xs mt-0.5" style={{ color: '#9B8A60' }}>
              {project.pages.length} page{project.pages.length !== 1 ? 's' : ''} · Updated {formatDate(project.updatedAt)}
            </p>
            {project.publishedVersion &&
            <p className="text-xs mt-0.5" style={{ color: '#16A34A' }}>
                Published {formatDate(project.publishedVersion.publishedAt)}
              </p>
            }
          </div>
          {/* Menu */}
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: '#9B8A60' }}
              onMouseEnter={(e) => {(e.currentTarget as HTMLElement).style.background = '#F2EDE3';}}
              onMouseLeave={(e) => {(e.currentTarget as HTMLElement).style.background = 'transparent';}}>
              
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            {showMenu &&
            <div className="absolute right-0 top-8 z-20 rounded-xl shadow-xl py-1 min-w-44" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8' }} onMouseLeave={() => setShowMenu(false)}>
                {[
              { label: 'Edit', icon: '✏️', action: onEdit },
              { label: 'Preview', icon: '👁️', action: onPreview },
              { label: project.published ? 'Unpublish' : 'Publish', icon: project.published ? '⏸️' : '🚀', action: project.published ? onUnpublish : onPublish },
              { label: 'Version History', icon: '🕐', action: onVersions },
              { label: 'Custom Domain', icon: '🌐', action: onDomain },
              { label: 'Duplicate', icon: '⧉', action: onDuplicate },
              { label: 'Export', icon: '📦', action: onExport },
              { label: 'Delete', icon: '🗑️', action: onDelete }].
              map((item) =>
              <button
                key={item.label}
                onClick={() => {item.action();setShowMenu(false);}}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-yellow-50 transition-colors text-left"
                style={{ color: item.label === 'Delete' ? '#dc2626' : '#2D2010' }}>
                
                    <span>{item.icon}</span>
                    {item.label}
                  </button>
              )}
              </div>
            }
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 mt-3">
          <button onClick={onEdit} className="flex-1 py-1.5 rounded-lg text-xs font-semibold" style={{ background: '#C9A84C', color: '#1A1208' }}>Edit</button>
          <button
            onClick={project.published ? onUnpublish : onPublish}
            className="flex-1 py-1.5 rounded-lg text-xs font-medium"
            style={{ background: project.published ? '#F0FDF4' : '#F2EDE3', color: project.published ? '#16A34A' : '#5C4A1E', border: `1px solid ${project.published ? '#BBF7D0' : '#E8DFC8'}` }}>
            
            {project.published ? 'Unpublish' : 'Publish'}
          </button>
        </div>
      </div>
    </div>);

}

// ─── Create Website Modal ─────────────────────────────────────────────────────
function CreateWebsiteModal({ onClose, onCreate }: {onClose: () => void;onCreate: (name: string) => void;}) {
  const [name, setName] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="rounded-2xl shadow-2xl w-full max-w-md mx-4" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8' }}>
        <div className="px-6 py-5 border-b" style={{ borderColor: '#E8DFC8' }}>
          <h2 className="font-bold text-lg" style={{ color: '#2D2010' }}>Create New Website</h2>
          <p className="text-sm mt-1" style={{ color: '#9B8A60' }}>Start building your new website from scratch.</p>
        </div>
        <div className="p-6">
          <label className="block text-sm font-semibold mb-2" style={{ color: '#5C4A1E' }}>Website Name</label>
          <input
            autoFocus
            type="text"
            placeholder="e.g. My Business Website"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {if (e.key === 'Enter' && name.trim()) onCreate(name.trim());if (e.key === 'Escape') onClose();}}
            className="w-full px-4 py-3 rounded-xl text-sm"
            style={{ background: '#FAF7F2', border: '1px solid #E8DFC8', color: '#2D2010', outline: 'none' }} />
          
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#F2EDE3', color: '#5C4A1E', border: '1px solid #E8DFC8' }}>Cancel</button>
          <button onClick={() => name.trim() && onCreate(name.trim())} disabled={!name.trim()} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ background: '#C9A84C', color: '#1A1208', opacity: name.trim() ? 1 : 0.5 }}>
            Create Website
          </button>
        </div>
      </div>
    </div>);

}

// ─── Dashboard Page ───────────────────────────────────────────────────────────
export default function BuilderDashboard() {
  const [projects, setProjects] = useState<BuilderProject[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [previewProject, setPreviewProject] = useState<BuilderProject | null>(null);
  const [publishProject, setPublishProject] = useState<BuilderProject | null>(null);
  const [domainProject, setDomainProject] = useState<BuilderProject | null>(null);
  const [versionsProject, setVersionsProject] = useState<BuilderProject | null>(null);
  const [mounted, setMounted] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMounted(true);
    const loaded = loadProjects();

    // ─── Ensure Rani Kamlapati project always exists with populated Home page ─
    const raniIdx = loaded.findIndex((p) => p.name === 'Rani Kamlapati');

    if (raniIdx === -1) {
      // Create fresh project with populated Home page
      const homePage = createRaniKamlapatiHomePage();
      const raniKamlapatiProject: BuilderProject = {
        id: generateProjectId(),
        name: 'Rani Kamlapati',
        pages: [
        homePage,
        {
          id: generatePageId(),
          name: 'About',
          isHomepage: false,
          seo: { slug: '/about', title: 'About – Rani Kamlapati', metaDescription: 'Learn about Rani Kamlapati', ogImage: '' },
          elements: {},
          rootChildren: [],
          cssClasses: {}
        },
        {
          id: generatePageId(),
          name: 'Services',
          isHomepage: false,
          seo: { slug: '/services', title: 'Services – Rani Kamlapati', metaDescription: 'Our services', ogImage: '' },
          elements: {},
          rootChildren: [],
          cssClasses: {}
        },
        {
          id: generatePageId(),
          name: 'Gallery',
          isHomepage: false,
          seo: { slug: '/gallery', title: 'Gallery – Rani Kamlapati', metaDescription: 'Photo gallery', ogImage: '' },
          elements: {},
          rootChildren: [],
          cssClasses: {}
        },
        {
          id: generatePageId(),
          name: 'Contact',
          isHomepage: false,
          seo: { slug: '/contact', title: 'Contact – Rani Kamlapati', metaDescription: 'Get in touch with us', ogImage: '' },
          elements: {},
          rootChildren: [],
          cssClasses: {}
        }],

        components: [],
        assets: [],
        published: false,
        createdAt: Date.now() - 86400000 * 2,
        updatedAt: Date.now() - 3600000
      };
      const newProjects = [raniKamlapatiProject, ...loaded];
      saveProjects(newProjects);
      setProjects(newProjects);
    } else {
      // Project exists — check if Home page has content; if not, populate it
      const existingProject = loaded[raniIdx];
      const homePageIdx = existingProject.pages.findIndex((p) => p.isHomepage || p.name === 'Home');
      if (homePageIdx !== -1 && Object.keys(existingProject.pages[homePageIdx].elements || {}).length === 0) {
        const populatedHome = createRaniKamlapatiHomePage();
        // Preserve the existing page id
        populatedHome.id = existingProject.pages[homePageIdx].id;
        const updatedPages = [...existingProject.pages];
        updatedPages[homePageIdx] = populatedHome;
        const updatedProject = { ...existingProject, pages: updatedPages, updatedAt: Date.now() };
        const updatedLoaded = [...loaded];
        updatedLoaded[raniIdx] = updatedProject;
        saveProjects(updatedLoaded);
        setProjects(updatedLoaded);
      } else {
        setProjects(loaded);
      }
    }
  }, []);

  const updateProjects = (updated: BuilderProject[]) => {
    setProjects(updated);
    saveProjects(updated);
  };

  const handleCreate = (name: string) => {
    const newProject: BuilderProject = {
      id: generateProjectId(),
      name,
      pages: [{ id: generatePageId(), name: 'Home', isHomepage: true, seo: { slug: '/', title: name, metaDescription: '', ogImage: '' }, elements: {}, rootChildren: [], cssClasses: {} }],
      components: [],
      assets: [],
      published: false,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    const updated = [newProject, ...projects];
    updateProjects(updated);
    setShowCreate(false);
    try {
      localStorage.setItem('builder_project', JSON.stringify({ pages: newProject.pages, components: [], assets: [], activePageId: newProject.pages[0].id }));
      localStorage.setItem('builder_active_project_id', newProject.id);
    } catch {/* ignore */}
    window.location.href = '/builder/editor';
  };

  const handleEdit = (project: BuilderProject) => {
    try {
      localStorage.setItem('builder_project', JSON.stringify({ pages: project.pages, components: project.components, assets: project.assets, activePageId: project.pages[0]?.id, designTokens: project.designTokens }));
      localStorage.setItem('builder_active_project_id', project.id);
    } catch {/* ignore */}
    window.location.href = '/builder/editor';
  };

  const handleDuplicate = (project: BuilderProject) => {
    const dup: BuilderProject = {
      ...JSON.parse(JSON.stringify(project)),
      id: generateProjectId(),
      name: project.name + ' Copy',
      published: false,
      publishedVersion: undefined,
      publishHistory: [],
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    updateProjects([...projects, dup]);
  };

  const handleDelete = (id: string) => {
    updateProjects(projects.filter((p) => p.id !== id));
  };

  const handleUnpublish = (id: string) => {
    updateProjects(projects.map((p) => p.id === id ? { ...p, published: false, updatedAt: Date.now() } : p));
  };

  const handlePublished = (projectId: string, updated: BuilderProject) => {
    updateProjects(projects.map((p) => p.id === projectId ? updated : p));
    setPublishProject(null);
  };

  const handleExport = (project: BuilderProject) => {
    const json = exportProjectJson(project);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name.replace(/\s+/g, '-').toLowerCase()}-export.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const imported = importProjectJson(text);
      if (imported) {
        const newProject: BuilderProject = {
          ...imported,
          id: generateProjectId(),
          name: imported.name + ' (Imported)',
          published: false,
          createdAt: Date.now(),
          updatedAt: Date.now()
        };
        updateProjects([newProject, ...projects]);
      } else {
        alert('Invalid project file. Please select a valid exported project JSON.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleDomainSave = (projectId: string, domain: CustomDomain) => {
    updateProjects(projects.map((p) => p.id === projectId ? { ...p, customDomain: domain, updatedAt: Date.now() } : p));
  };

  const handleRestoreVersion = (projectId: string, version: PublishedVersion) => {
    updateProjects(projects.map((p) => p.id === projectId ? { ...p, publishedVersion: version, updatedAt: Date.now() } : p));
  };

  const filtered = projects.filter((p) => !search.trim() || p.name.toLowerCase().includes(search.toLowerCase()));

  if (!mounted) return null;

  return (
    <div className="min-h-screen" style={{ background: '#FAF7F2' }}>
      {/* Header */}
      <header className="border-b sticky top-0 z-10" style={{ background: '#FFFFFF', borderColor: '#E8DFC8' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #C9A84C, #8B6914)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="font-bold text-base" style={{ color: '#2D2010' }}>Builder</span>
            </Link>
            <span className="text-sm" style={{ color: '#B8A070' }}>/ Dashboard</span>
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="#2D2010" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                placeholder="Search projects..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 rounded-xl text-sm"
                style={{ background: '#F2EDE3', border: '1px solid #E8DFC8', color: '#2D2010', outline: 'none', width: 200 }} />
              
            </div>

            {/* Import */}
            <button
              onClick={() => importRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
              style={{ background: '#F2EDE3', color: '#5C4A1E', border: '1px solid #E8DFC8' }}
              title="Import Project">
              
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Import
            </button>
            <input ref={importRef} type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />

            <Link
              href="/builder/editor"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
              style={{ background: '#F2EDE3', color: '#5C4A1E', border: '1px solid #E8DFC8' }}>
              
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
              Open Editor
            </Link>

            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
              style={{ background: '#C9A84C', color: '#1A1208' }}>
              
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></svg>
              Create Website
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
          { label: 'Total Projects', value: projects.length, icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', color: '#4F46E5' },
          { label: 'Published', value: projects.filter((p) => p.published).length, icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: '#16A34A' },
          { label: 'Drafts', value: projects.filter((p) => !p.published).length, icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z', color: '#D97706' },
          { label: 'Total Pages', value: projects.reduce((sum, p) => sum + p.pages.length, 0), icon: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6', color: '#E11D48' }].
          map((stat) =>
          <div key={stat.label} className="rounded-2xl p-5 border" style={{ background: '#FFFFFF', borderColor: '#E8DFC8' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${stat.color}15` }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d={stat.icon} stroke={stat.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold" style={{ color: '#2D2010' }}>{stat.value}</p>
              <p className="text-xs mt-0.5" style={{ color: '#9B8A60' }}>{stat.label}</p>
            </div>
          )}
        </div>

        {/* Projects Grid */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-bold text-lg" style={{ color: '#2D2010' }}>
            {search ? `Results for "${search}"` : 'All Projects'}
            <span className="ml-2 text-sm font-normal" style={{ color: '#9B8A60' }}>({filtered.length})</span>
          </h2>
        </div>

        {filtered.length === 0 ?
        <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-5" style={{ background: '#F2EDE3' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>
            <h3 className="font-bold text-lg mb-2" style={{ color: '#2D2010' }}>{search ? 'No projects found' : 'No projects yet'}</h3>
            <p className="text-sm mb-6" style={{ color: '#9B8A60' }}>{search ? 'Try a different search term.' : 'Create your first website to get started.'}</p>
            {!search &&
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold" style={{ background: '#C9A84C', color: '#1A1208' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></svg>
                Create Your First Website
              </button>
          }
          </div> :

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {/* Create new card */}
            <button
            onClick={() => setShowCreate(true)}
            className="rounded-2xl border-2 border-dashed flex flex-col items-center justify-center transition-all"
            style={{ minHeight: 280, borderColor: '#E0D4B0', background: 'transparent' }}
            onMouseEnter={(e) => {(e.currentTarget as HTMLElement).style.borderColor = '#C9A84C';(e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.04)';}}
            onMouseLeave={(e) => {(e.currentTarget as HTMLElement).style.borderColor = '#E0D4B0';(e.currentTarget as HTMLElement).style.background = 'transparent';}}>
            
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: '#F2EDE3' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round" /></svg>
              </div>
              <p className="font-semibold text-sm" style={{ color: '#5C4A1E' }}>New Website</p>
              <p className="text-xs mt-1" style={{ color: '#B8A070' }}>Start from scratch</p>
            </button>

            {filtered.map((project, i) =>
          <ProjectCard
            key={project.id}
            project={project}
            index={i}
            onEdit={() => handleEdit(project)}
            onDuplicate={() => handleDuplicate(project)}
            onDelete={() => handleDelete(project.id)}
            onPublish={() => setPublishProject(project)}
            onUnpublish={() => handleUnpublish(project.id)}
            onPreview={() => setPreviewProject(project)}
            onExport={() => handleExport(project)}
            onDomain={() => setDomainProject(project)}
            onVersions={() => setVersionsProject(project)} />

          )}
          </div>
        }
      </main>

      {/* Modals */}
      {showCreate && <CreateWebsiteModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}

      {publishProject &&
      <DashboardPublishModal
        project={publishProject}
        onClose={() => setPublishProject(null)}
        onPublished={(updated) => handlePublished(publishProject.id, updated)} />

      }

      {domainProject &&
      <CustomDomainModal
        project={domainProject}
        onClose={() => setDomainProject(null)}
        onSave={(domain) => {handleDomainSave(domainProject.id, domain);setDomainProject(null);}} />

      }

      {versionsProject &&
      <VersionHistoryModal
        project={versionsProject}
        onClose={() => setVersionsProject(null)}
        onRestore={(version) => handleRestoreVersion(versionsProject.id, version)} />

      }

      {/* Preview Modal */}
      {previewProject &&
      <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'rgba(0,0,0,0.85)' }}>
          <div className="flex items-center gap-3 px-4 h-12 flex-shrink-0" style={{ background: '#1A1208' }}>
            <span className="text-sm font-semibold" style={{ color: '#C9A84C' }}>{previewProject.name} — Preview</span>
            <div className="flex-1" />
            <button onClick={() => handleEdit(previewProject)} className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold mr-2" style={{ background: 'rgba(201,168,76,0.2)', color: '#C9A84C' }}>Edit</button>
            <button onClick={() => setPreviewProject(null)} className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold" style={{ background: '#C9A84C', color: '#1A1208' }}>✕ Close</button>
          </div>
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="rounded-xl overflow-hidden" style={{ width: 1024, maxWidth: '100%', background: '#fff', boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }}>
              <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ background: '#f5f5f5', borderColor: '#e0e0e0' }}>
                <div className="flex gap-1.5">
                  {['#ff5f57', '#febc2e', '#28c840'].map((c) => <div key={c} className="w-3 h-3 rounded-full" style={{ background: c }} />)}
                </div>
                <div className="flex-1 mx-4 px-3 py-1 rounded text-xs text-center" style={{ background: '#fff', border: '1px solid #e0e0e0', color: '#666' }}>
                  {previewProject.pages[0]?.seo?.slug || '/'}
                </div>
              </div>
              <div className="flex items-center justify-center py-16" style={{ background: '#fafafa', minHeight: 400 }}>
                <div className="text-center">
                  <p className="text-2xl font-bold mb-2" style={{ color: '#2D2010' }}>{previewProject.name}</p>
                  <p className="text-sm mb-4" style={{ color: '#9B8A60' }}>Open in editor to see the full preview</p>
                  <button onClick={() => handleEdit(previewProject)} className="px-6 py-2.5 rounded-xl text-sm font-semibold" style={{ background: '#C9A84C', color: '#1A1208' }}>
                    Open in Editor
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    </div>);

}

// ─── Rani Kamlapati Home Page Element Tree ────────────────────────────────────
function makeEl(
id: string,
type: ElementType,
label: string,
parentId: string | null,
children: string[],
content: string,
styles: BuilderElement['styles'],
attributes: BuilderElement['attributes'] = {})
: BuilderElement {
  return { id, type, label, parentId, children, content, styles, attributes, hidden: false, locked: false, cssClasses: [] };
}

function createRaniKamlapatiHomePage(): BuilderPage {
  const elements: Record<string, BuilderElement> = {};
  const rootChildren: string[] = [];

  function add(el: BuilderElement) {elements[el.id] = el;}

  // ── NAVBAR SECTION ──────────────────────────────────────────────────────────
  const navSection = makeEl('rk-nav-section', 'section', 'Navbar', null, ['rk-nav-container'], '', {
    backgroundColor: '#1a1a2e', padding: '0', width: '100%', display: 'block', position: 'sticky', top: '0', zIndex: '100'
  });
  const navContainer = makeEl('rk-nav-container', 'container', 'Nav Container', 'rk-nav-section', ['rk-nav-logo', 'rk-nav-links', 'rk-nav-btn'], '', {
    maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px'
  });
  const navLogo = makeEl('rk-nav-logo', 'heading', 'Logo', 'rk-nav-container', [], 'EventSphere', {
    fontSize: '22px', fontWeight: '800', color: '#C9A84C', margin: '0', letterSpacing: '-0.5px', display: 'block'
  });
  const navLinks = makeEl('rk-nav-links', 'div', 'Nav Links', 'rk-nav-container', ['rk-nav-link1', 'rk-nav-link2', 'rk-nav-link3', 'rk-nav-link4'], '', {
    display: 'flex', gap: '32px', alignItems: 'center'
  });
  const navLink1 = makeEl('rk-nav-link1', 'link', 'Nav Link Home', 'rk-nav-links', [], 'Home', { color: '#ffffff', fontSize: '14px', fontWeight: '500', textDecoration: 'none', display: 'inline-block' }, { href: '/' });
  const navLink2 = makeEl('rk-nav-link2', 'link', 'Nav Link Events', 'rk-nav-links', [], 'Events', { color: '#ffffff', fontSize: '14px', fontWeight: '500', textDecoration: 'none', display: 'inline-block' }, { href: '#discover' });
  const navLink3 = makeEl('rk-nav-link3', 'link', 'Nav Link About', 'rk-nav-links', [], 'About', { color: '#ffffff', fontSize: '14px', fontWeight: '500', textDecoration: 'none', display: 'inline-block' }, { href: '#about' });
  const navLink4 = makeEl('rk-nav-link4', 'link', 'Nav Link Contact', 'rk-nav-links', [], 'Contact', { color: '#ffffff', fontSize: '14px', fontWeight: '500', textDecoration: 'none', display: 'inline-block' }, { href: '#contact' });
  const navBtn = makeEl('rk-nav-btn', 'button', 'Nav CTA Button', 'rk-nav-container', [], 'Create Event', {
    padding: '10px 20px', backgroundColor: '#C9A84C', color: '#1A1208', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'inline-block', border: 'none'
  });
  [navSection, navContainer, navLogo, navLinks, navLink1, navLink2, navLink3, navLink4, navBtn].forEach(add);
  rootChildren.push('rk-nav-section');

  // ── HERO SECTION ────────────────────────────────────────────────────────────
  const heroSection = makeEl('rk-hero-section', 'section', 'Hero Section', null, ['rk-hero-overlay', 'rk-hero-content'], '', {
    backgroundColor: '#1a1a2e', backgroundImage: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', minHeight: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', width: '100%', position: 'relative'
  });
  const heroOverlay = makeEl('rk-hero-overlay', 'div', 'Hero Overlay', 'rk-hero-section', [], '', {
    position: 'absolute', top: '0', left: '0', right: '0', bottom: '0', backgroundColor: 'rgba(0,0,0,0.4)', display: 'block'
  });
  const heroContent = makeEl('rk-hero-content', 'container', 'Hero Content', 'rk-hero-section', ['rk-hero-badge', 'rk-hero-title', 'rk-hero-subtitle', 'rk-hero-btns'], '', {
    maxWidth: '800px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: '2', display: 'block'
  });
  const heroBadge = makeEl('rk-hero-badge', 'text', 'Hero Badge', 'rk-hero-content', [], '🎉 Discover Amazing Events Near You', {
    display: 'inline-block', backgroundColor: 'rgba(201,168,76,0.2)', color: '#C9A84C', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', marginBottom: '24px', border: '1px solid rgba(201,168,76,0.4)'
  });
  const heroTitle = makeEl('rk-hero-title', 'heading', 'Hero Title', 'rk-hero-content', [], 'Find & Book Events That Matter To You', {
    fontSize: '52px', fontWeight: '800', color: '#ffffff', lineHeight: '1.15', marginBottom: '20px', display: 'block'
  });
  const heroSubtitle = makeEl('rk-hero-subtitle', 'paragraph', 'Hero Subtitle', 'rk-hero-content', [], 'Explore thousands of events — concerts, workshops, sports, festivals and more. Connect with your community and create unforgettable memories.', {
    fontSize: '18px', color: 'rgba(255,255,255,0.8)', lineHeight: '1.7', marginBottom: '36px', display: 'block'
  });
  const heroBtns = makeEl('rk-hero-btns', 'div', 'Hero Buttons', 'rk-hero-content', ['rk-hero-btn1', 'rk-hero-btn2'], '', {
    display: 'flex', gap: '16px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap'
  });
  const heroBtn1 = makeEl('rk-hero-btn1', 'button', 'Explore Events Button', 'rk-hero-btns', [], 'Explore Events', {
    padding: '14px 32px', backgroundColor: '#C9A84C', color: '#1A1208', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', display: 'inline-block', border: 'none'
  });
  const heroBtn2 = makeEl('rk-hero-btn2', 'button', 'Create Event Button', 'rk-hero-btns', [], 'Create an Event', {
    padding: '14px 32px', backgroundColor: 'transparent', color: '#ffffff', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', display: 'inline-block', border: '2px solid rgba(255,255,255,0.5)'
  });
  [heroSection, heroOverlay, heroContent, heroBadge, heroTitle, heroSubtitle, heroBtns, heroBtn1, heroBtn2].forEach(add);
  rootChildren.push('rk-hero-section');

  // ── CATEGORY BAR ────────────────────────────────────────────────────────────
  const catSection = makeEl('rk-cat-section', 'section', 'Category Bar', null, ['rk-cat-container'], '', {
    backgroundColor: '#ffffff', padding: '16px 0', borderBottom: '1px solid #E8DFC8', width: '100%', display: 'block'
  });
  const catContainer = makeEl('rk-cat-container', 'container', 'Category Container', 'rk-cat-section', ['rk-cat-all', 'rk-cat-music', 'rk-cat-sports', 'rk-cat-food', 'rk-cat-arts', 'rk-cat-tech', 'rk-cat-wellness'], '', {
    maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'flex', gap: '12px', alignItems: 'center', overflowX: 'auto'
  });
  const categories = [
  { id: 'rk-cat-all', label: '🎯 All Events', active: true },
  { id: 'rk-cat-music', label: '🎵 Music', active: false },
  { id: 'rk-cat-sports', label: '⚽ Sports', active: false },
  { id: 'rk-cat-food', label: '🍽️ Food & Drink', active: false },
  { id: 'rk-cat-arts', label: '🎨 Arts', active: false },
  { id: 'rk-cat-tech', label: '💻 Technology', active: false },
  { id: 'rk-cat-wellness', label: '🧘 Wellness', active: false }];

  categories.forEach((cat) => {
    add(makeEl(cat.id, 'button', cat.label, 'rk-cat-container', [], cat.label, {
      padding: '8px 18px', backgroundColor: cat.active ? '#C9A84C' : '#F2EDE3', color: cat.active ? '#1A1208' : '#5C4A1E', borderRadius: '20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'inline-block', border: 'none', whiteSpace: 'nowrap'
    }));
  });
  add(catSection);add(catContainer);
  rootChildren.push('rk-cat-section');

  // ── EVENTS GRID SECTION ─────────────────────────────────────────────────────
  const eventsSection = makeEl('rk-events-section', 'section', 'Events Grid', null, ['rk-events-container'], '', {
    backgroundColor: '#FAF7F2', padding: '60px 0', width: '100%', display: 'block'
  });
  const eventsContainer = makeEl('rk-events-container', 'container', 'Events Container', 'rk-events-section', ['rk-events-header', 'rk-events-grid'], '', {
    maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'block'
  });
  const eventsHeader = makeEl('rk-events-header', 'div', 'Events Header', 'rk-events-container', ['rk-events-title', 'rk-events-subtitle'], '', {
    marginBottom: '40px', display: 'block'
  });
  const eventsTitle = makeEl('rk-events-title', 'heading', 'Events Title', 'rk-events-header', [], 'Upcoming Events', {
    fontSize: '36px', fontWeight: '800', color: '#2D2010', marginBottom: '8px', display: 'block'
  });
  const eventsSubtitle = makeEl('rk-events-subtitle', 'paragraph', 'Events Subtitle', 'rk-events-header', [], 'Discover the best events happening around you this week', {
    fontSize: '16px', color: '#9B8A60', display: 'block', margin: '0'
  });
  const eventsGrid = makeEl('rk-events-grid', 'grid-container', 'Events Grid', 'rk-events-container', ['rk-event-1', 'rk-event-2', 'rk-event-3', 'rk-event-4', 'rk-event-5', 'rk-event-6'], '', {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px'
  });
  const eventData = [
  { id: 'rk-event-1', title: 'Bhopal Music Festival 2025', date: 'Dec 15, 2025', location: 'Bhopal, MP', category: 'Music', color: '#667eea', img: "https://img.rocket.new/generatedImages/rocket_gen_img_1be46dcd5-1776462662016.png" },
  { id: 'rk-event-2', title: 'Tech Summit India', date: 'Jan 10, 2026', location: 'Indore, MP', category: 'Technology', color: '#4facfe', img: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=250&fit=crop' },
  { id: 'rk-event-3', title: 'Cultural Dance Night', date: 'Dec 20, 2025', location: 'Bhopal, MP', category: 'Arts', color: '#f093fb', img: 'https://images.unsplash.com/photo-1504609813442-a8924e83f76e?w=400&h=250&fit=crop' },
  { id: 'rk-event-4', title: 'Food & Flavors Fest', date: 'Jan 5, 2026', location: 'Jabalpur, MP', category: 'Food', color: '#43e97b', img: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400&h=250&fit=crop' },
  { id: 'rk-event-5', title: 'Marathon for a Cause', date: 'Jan 18, 2026', location: 'Bhopal, MP', category: 'Sports', color: '#fa709a', img: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=250&fit=crop' },
  { id: 'rk-event-6', title: 'Yoga & Wellness Retreat', date: 'Feb 2, 2026', location: 'Pachmarhi, MP', category: 'Wellness', color: '#a18cd1', img: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=250&fit=crop' }];

  eventData.forEach((ev) => {
    const card = makeEl(ev.id, 'div', ev.title, 'rk-events-grid', [`${ev.id}-img`, `${ev.id}-body`], '', {
      backgroundColor: '#ffffff', borderRadius: '16px', overflow: 'hidden', border: '1px solid #E8DFC8', display: 'block', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    });
    const cardImg = makeEl(`${ev.id}-img`, 'image', `${ev.title} Image`, ev.id, [], '', {
      width: '100%', height: '180px', objectFit: 'cover', display: 'block'
    }, { src: ev.img, alt: ev.title });
    const cardBody = makeEl(`${ev.id}-body`, 'div', 'Card Body', ev.id, [`${ev.id}-cat`, `${ev.id}-title`, `${ev.id}-meta`, `${ev.id}-btn`], '', {
      padding: '16px', display: 'block'
    });
    const cardCat = makeEl(`${ev.id}-cat`, 'text', 'Category Badge', `${ev.id}-body`, [], ev.category, {
      display: 'inline-block', backgroundColor: `${ev.color}20`, color: ev.color, padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', marginBottom: '8px'
    });
    const cardTitle = makeEl(`${ev.id}-title`, 'heading', 'Event Title', `${ev.id}-body`, [], ev.title, {
      fontSize: '16px', fontWeight: '700', color: '#2D2010', marginBottom: '8px', lineHeight: '1.3', display: 'block'
    });
    const cardMeta = makeEl(`${ev.id}-meta`, 'paragraph', 'Event Meta', `${ev.id}-body`, [], `📅 ${ev.date}  •  📍 ${ev.location}`, {
      fontSize: '12px', color: '#9B8A60', marginBottom: '12px', display: 'block'
    });
    const cardBtn = makeEl(`${ev.id}-btn`, 'button', 'Book Button', `${ev.id}-body`, [], 'Book Now', {
      padding: '8px 16px', backgroundColor: '#C9A84C', color: '#1A1208', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'inline-block', border: 'none'
    });
    [card, cardImg, cardBody, cardCat, cardTitle, cardMeta, cardBtn].forEach(add);
  });
  [eventsSection, eventsContainer, eventsHeader, eventsTitle, eventsSubtitle, eventsGrid].forEach(add);
  rootChildren.push('rk-events-section');

  // ── FEATURED BANNER ─────────────────────────────────────────────────────────
  const featSection = makeEl('rk-feat-section', 'section', 'Featured Banner', null, ['rk-feat-container'], '', {
    backgroundColor: '#1a1a2e', padding: '60px 0', width: '100%', display: 'block'
  });
  const featContainer = makeEl('rk-feat-container', 'container', 'Featured Container', 'rk-feat-section', ['rk-feat-title', 'rk-feat-subtitle', 'rk-feat-stats'], '', {
    maxWidth: '1200px', margin: '0 auto', padding: '0 24px', textAlign: 'center', display: 'block'
  });
  const featTitle = makeEl('rk-feat-title', 'heading', 'Featured Title', 'rk-feat-container', [], 'EventSphere Premium', {
    fontSize: '40px', fontWeight: '800', color: '#C9A84C', marginBottom: '12px', display: 'block'
  });
  const featSubtitle = makeEl('rk-feat-subtitle', 'paragraph', 'Featured Subtitle', 'rk-feat-container', [], 'Join thousands of event organizers and attendees on India\'s fastest growing event platform', {
    fontSize: '17px', color: 'rgba(255,255,255,0.75)', marginBottom: '48px', display: 'block'
  });
  const featStats = makeEl('rk-feat-stats', 'div', 'Stats Row', 'rk-feat-container', ['rk-stat-1', 'rk-stat-2', 'rk-stat-3', 'rk-stat-4'], '', {
    display: 'flex', justifyContent: 'center', gap: '48px', flexWrap: 'wrap'
  });
  const stats = [
  { id: 'rk-stat-1', num: '50K+', label: 'Events Hosted' },
  { id: 'rk-stat-2', num: '2M+', label: 'Tickets Sold' },
  { id: 'rk-stat-3', num: '500+', label: 'Cities Covered' },
  { id: 'rk-stat-4', num: '98%', label: 'Satisfaction Rate' }];

  stats.forEach((s) => {
    const statBox = makeEl(s.id, 'div', s.label, 'rk-feat-stats', [`${s.id}-num`, `${s.id}-lbl`], '', { textAlign: 'center', display: 'block' });
    const statNum = makeEl(`${s.id}-num`, 'heading', 'Stat Number', s.id, [], s.num, { fontSize: '42px', fontWeight: '800', color: '#C9A84C', display: 'block', margin: '0 0 4px 0' });
    const statLbl = makeEl(`${s.id}-lbl`, 'text', 'Stat Label', s.id, [], s.label, { fontSize: '14px', color: 'rgba(255,255,255,0.6)', display: 'block' });
    [statBox, statNum, statLbl].forEach(add);
  });
  [featSection, featContainer, featTitle, featSubtitle, featStats].forEach(add);
  rootChildren.push('rk-feat-section');

  // ── BROWSE BY INTEREST ──────────────────────────────────────────────────────
  const interestSection = makeEl('rk-interest-section', 'section', 'Browse by Interest', null, ['rk-interest-container'], '', {
    backgroundColor: '#FAF7F2', padding: '60px 0', width: '100%', display: 'block'
  });
  const interestContainer = makeEl('rk-interest-container', 'container', 'Interest Container', 'rk-interest-section', ['rk-interest-title', 'rk-interest-grid'], '', {
    maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'block'
  });
  const interestTitle = makeEl('rk-interest-title', 'heading', 'Interest Title', 'rk-interest-container', [], 'Browse by Interest', {
    fontSize: '36px', fontWeight: '800', color: '#2D2010', marginBottom: '32px', display: 'block'
  });
  const interestGrid = makeEl('rk-interest-grid', 'div', 'Interest Grid', 'rk-interest-container', ['rk-int-1', 'rk-int-2', 'rk-int-3', 'rk-int-4', 'rk-int-5', 'rk-int-6'], '', {
    display: 'flex', gap: '16px', flexWrap: 'wrap'
  });
  const interests = [
  { id: 'rk-int-1', emoji: '🎵', label: 'Music & Concerts', color: '#667eea' },
  { id: 'rk-int-2', emoji: '⚽', label: 'Sports & Fitness', color: '#43e97b' },
  { id: 'rk-int-3', emoji: '🎨', label: 'Arts & Culture', color: '#f093fb' },
  { id: 'rk-int-4', emoji: '🍽️', label: 'Food & Dining', color: '#fa709a' },
  { id: 'rk-int-5', emoji: '💻', label: 'Tech & Innovation', color: '#4facfe' },
  { id: 'rk-int-6', emoji: '🧘', label: 'Health & Wellness', color: '#a18cd1' }];

  interests.forEach((int) => {
    const chip = makeEl(int.id, 'div', int.label, 'rk-interest-grid', [`${int.id}-emoji`, `${int.id}-lbl`], '', {
      display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 22px', backgroundColor: '#ffffff', borderRadius: '50px', border: `2px solid ${int.color}30`, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    });
    const intEmoji = makeEl(`${int.id}-emoji`, 'text', 'Emoji', int.id, [], int.emoji, { fontSize: '20px', display: 'inline-block' });
    const intLbl = makeEl(`${int.id}-lbl`, 'text', 'Label', int.id, [], int.label, { fontSize: '14px', fontWeight: '600', color: '#2D2010', display: 'inline-block' });
    [chip, intEmoji, intLbl].forEach(add);
  });
  [interestSection, interestContainer, interestTitle, interestGrid].forEach(add);
  rootChildren.push('rk-interest-section');

  // ── ORGANIZER BANNER ────────────────────────────────────────────────────────
  const orgSection = makeEl('rk-org-section', 'section', 'Organizer Banner', null, ['rk-org-container'], '', {
    backgroundColor: '#F5EFE6', padding: '60px 0', width: '100%', display: 'block'
  });
  const orgContainer = makeEl('rk-org-container', 'container', 'Organizer Container', 'rk-org-section', ['rk-org-finder', 'rk-org-create'], '', {
    maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px'
  });
  const orgFinder = makeEl('rk-org-finder', 'div', 'Event Finder Card', 'rk-org-container', ['rk-org-finder-title', 'rk-org-finder-text', 'rk-org-finder-btn'], '', {
    backgroundColor: '#ffffff', borderRadius: '20px', padding: '40px', display: 'block', border: '1px solid #E8DFC8'
  });
  const orgFinderTitle = makeEl('rk-org-finder-title', 'heading', 'Finder Title', 'rk-org-finder', [], '🔍 Find Events Near You', { fontSize: '24px', fontWeight: '800', color: '#2D2010', marginBottom: '12px', display: 'block' });
  const orgFinderText = makeEl('rk-org-finder-text', 'paragraph', 'Finder Text', 'rk-org-finder', [], 'Discover local events, workshops, and experiences happening in your city right now.', { fontSize: '15px', color: '#9B8A60', marginBottom: '24px', lineHeight: '1.6', display: 'block' });
  const orgFinderBtn = makeEl('rk-org-finder-btn', 'button', 'Finder Button', 'rk-org-finder', [], 'Find Events', { padding: '12px 28px', backgroundColor: '#C9A84C', color: '#1A1208', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'inline-block', border: 'none' });
  const orgCreate = makeEl('rk-org-create', 'div', 'Create Event Card', 'rk-org-container', ['rk-org-create-title', 'rk-org-create-text', 'rk-org-create-btn'], '', {
    backgroundColor: '#1a1a2e', borderRadius: '20px', padding: '40px', display: 'block'
  });
  const orgCreateTitle = makeEl('rk-org-create-title', 'heading', 'Create Title', 'rk-org-create', [], '🚀 Organize an Event', { fontSize: '24px', fontWeight: '800', color: '#C9A84C', marginBottom: '12px', display: 'block' });
  const orgCreateText = makeEl('rk-org-create-text', 'paragraph', 'Create Text', 'rk-org-create', [], 'Create, promote, and manage your events with powerful tools. Reach thousands of attendees.', { fontSize: '15px', color: 'rgba(255,255,255,0.7)', marginBottom: '24px', lineHeight: '1.6', display: 'block' });
  const orgCreateBtn = makeEl('rk-org-create-btn', 'button', 'Create Button', 'rk-org-create', [], 'Start Organizing', { padding: '12px 28px', backgroundColor: '#C9A84C', color: '#1A1208', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'inline-block', border: 'none' });
  [orgSection, orgContainer, orgFinder, orgFinderTitle, orgFinderText, orgFinderBtn, orgCreate, orgCreateTitle, orgCreateText, orgCreateBtn].forEach(add);
  rootChildren.push('rk-org-section');

  // ── NEWS SECTION ────────────────────────────────────────────────────────────
  const newsSection = makeEl('rk-news-section', 'section', 'News & Updates', null, ['rk-news-container'], '', {
    backgroundColor: '#ffffff', padding: '60px 0', width: '100%', display: 'block'
  });
  const newsContainer = makeEl('rk-news-container', 'container', 'News Container', 'rk-news-section', ['rk-news-title', 'rk-news-grid'], '', {
    maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'block'
  });
  const newsTitle = makeEl('rk-news-title', 'heading', 'News Title', 'rk-news-container', [], 'Latest News & Updates', {
    fontSize: '36px', fontWeight: '800', color: '#2D2010', marginBottom: '32px', display: 'block'
  });
  const newsGrid = makeEl('rk-news-grid', 'div', 'News Grid', 'rk-news-container', ['rk-news-1', 'rk-news-2', 'rk-news-3'], '', {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px'
  });
  const newsItems = [
  { id: 'rk-news-1', title: 'EventSphere Launches in 50 New Cities', date: 'Nov 28, 2025', excerpt: 'We are excited to announce our expansion to 50 new cities across India, bringing world-class events closer to you.' },
  { id: 'rk-news-2', title: 'New Feature: Group Booking Now Available', date: 'Nov 20, 2025', excerpt: 'Book tickets for your entire group with our new group booking feature. Get exclusive discounts for groups of 10+.' },
  { id: 'rk-news-3', title: 'EventSphere Partners with Top Venues', date: 'Nov 15, 2025', excerpt: 'We have partnered with 200+ premium venues across India to bring you the best event experiences.' }];

  newsItems.forEach((n) => {
    const card = makeEl(n.id, 'div', n.title, 'rk-news-grid', [`${n.id}-date`, `${n.id}-title`, `${n.id}-text`], '', {
      backgroundColor: '#FAF7F2', borderRadius: '16px', padding: '24px', border: '1px solid #E8DFC8', display: 'block'
    });
    const nDate = makeEl(`${n.id}-date`, 'text', 'Date', n.id, [], n.date, { fontSize: '12px', color: '#C9A84C', fontWeight: '600', display: 'block', marginBottom: '8px' });
    const nTitle = makeEl(`${n.id}-title`, 'heading', 'News Title', n.id, [], n.title, { fontSize: '18px', fontWeight: '700', color: '#2D2010', marginBottom: '10px', lineHeight: '1.4', display: 'block' });
    const nText = makeEl(`${n.id}-text`, 'paragraph', 'News Excerpt', n.id, [], n.excerpt, { fontSize: '14px', color: '#9B8A60', lineHeight: '1.6', display: 'block', margin: '0' });
    [card, nDate, nTitle, nText].forEach(add);
  });
  [newsSection, newsContainer, newsTitle, newsGrid].forEach(add);
  rootChildren.push('rk-news-section');

  // ── TESTIMONIALS ────────────────────────────────────────────────────────────
  const testSection = makeEl('rk-test-section', 'section', 'Testimonials', null, ['rk-test-container'], '', {
    backgroundColor: '#FAF7F2', padding: '60px 0', width: '100%', display: 'block'
  });
  const testContainer = makeEl('rk-test-container', 'container', 'Testimonials Container', 'rk-test-section', ['rk-test-title', 'rk-test-grid'], '', {
    maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'block'
  });
  const testTitle = makeEl('rk-test-title', 'heading', 'Testimonials Title', 'rk-test-container', [], 'What Our Users Say', {
    fontSize: '36px', fontWeight: '800', color: '#2D2010', marginBottom: '32px', textAlign: 'center', display: 'block'
  });
  const testGrid = makeEl('rk-test-grid', 'div', 'Testimonials Grid', 'rk-test-container', ['rk-test-1', 'rk-test-2', 'rk-test-3'], '', {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px'
  });
  const testimonials = [
  { id: 'rk-test-1', name: 'Priya Sharma', role: 'Event Attendee', text: 'EventSphere made it so easy to find and book events in Bhopal. I discovered amazing cultural events I never knew existed!', stars: '⭐⭐⭐⭐⭐' },
  { id: 'rk-test-2', name: 'Rahul Verma', role: 'Event Organizer', text: 'As an organizer, the platform tools are incredible. We sold out our tech summit in just 3 days using EventSphere.', stars: '⭐⭐⭐⭐⭐' },
  { id: 'rk-test-3', name: 'Anita Patel', role: 'Regular User', text: 'The best event discovery platform in India. The interface is clean, booking is seamless, and support is excellent.', stars: '⭐⭐⭐⭐⭐' }];

  testimonials.forEach((t) => {
    const card = makeEl(t.id, 'div', t.name, 'rk-test-grid', [`${t.id}-stars`, `${t.id}-text`, `${t.id}-name`, `${t.id}-role`], '', {
      backgroundColor: '#ffffff', borderRadius: '16px', padding: '28px', border: '1px solid #E8DFC8', display: 'block', boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    });
    const tStars = makeEl(`${t.id}-stars`, 'text', 'Stars', t.id, [], t.stars, { fontSize: '16px', display: 'block', marginBottom: '12px' });
    const tText = makeEl(`${t.id}-text`, 'paragraph', 'Testimonial Text', t.id, [], `"${t.text}"`, { fontSize: '14px', color: '#5C4A1E', lineHeight: '1.7', marginBottom: '16px', display: 'block', fontStyle: 'italic' });
    const tName = makeEl(`${t.id}-name`, 'text', 'Name', t.id, [], t.name, { fontSize: '15px', fontWeight: '700', color: '#2D2010', display: 'block' });
    const tRole = makeEl(`${t.id}-role`, 'text', 'Role', t.id, [], t.role, { fontSize: '12px', color: '#9B8A60', display: 'block' });
    [card, tStars, tText, tName, tRole].forEach(add);
  });
  [testSection, testContainer, testTitle, testGrid].forEach(add);
  rootChildren.push('rk-test-section');

  // ── JOIN US SECTION ─────────────────────────────────────────────────────────
  const joinSection = makeEl('rk-join-section', 'section', 'Join Us Section', null, ['rk-join-container'], '', {
    backgroundColor: '#1a1a2e', backgroundImage: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)', padding: '80px 0', width: '100%', display: 'block'
  });
  const joinContainer = makeEl('rk-join-container', 'container', 'Join Container', 'rk-join-section', ['rk-join-title', 'rk-join-text', 'rk-join-btns'], '', {
    maxWidth: '700px', margin: '0 auto', padding: '0 24px', textAlign: 'center', display: 'block'
  });
  const joinTitle = makeEl('rk-join-title', 'heading', 'Join Title', 'rk-join-container', [], 'Ready to Experience More?', {
    fontSize: '44px', fontWeight: '800', color: '#ffffff', marginBottom: '16px', lineHeight: '1.2', display: 'block'
  });
  const joinText = makeEl('rk-join-text', 'paragraph', 'Join Text', 'rk-join-container', [], 'Join over 2 million people who use EventSphere to discover, book, and enjoy the best events across India.', {
    fontSize: '17px', color: 'rgba(255,255,255,0.75)', marginBottom: '40px', lineHeight: '1.7', display: 'block'
  });
  const joinBtns = makeEl('rk-join-btns', 'div', 'Join Buttons', 'rk-join-container', ['rk-join-btn1', 'rk-join-btn2'], '', {
    display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'
  });
  const joinBtn1 = makeEl('rk-join-btn1', 'button', 'Get Started Button', 'rk-join-btns', [], 'Get Started Free', {
    padding: '16px 36px', backgroundColor: '#C9A84C', color: '#1A1208', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', display: 'inline-block', border: 'none'
  });
  const joinBtn2 = makeEl('rk-join-btn2', 'button', 'Learn More Button', 'rk-join-btns', [], 'Learn More', {
    padding: '16px 36px', backgroundColor: 'transparent', color: '#ffffff', borderRadius: '12px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', display: 'inline-block', border: '2px solid rgba(255,255,255,0.4)'
  });
  [joinSection, joinContainer, joinTitle, joinText, joinBtns, joinBtn1, joinBtn2].forEach(add);
  rootChildren.push('rk-join-section');

  // ── FOOTER ──────────────────────────────────────────────────────────────────
  const footerSection = makeEl('rk-footer-section', 'section', 'Footer', null, ['rk-footer-container', 'rk-footer-bottom'], '', {
    backgroundColor: '#0d0d1a', padding: '60px 0 0 0', width: '100%', display: 'block'
  });
  const footerContainer = makeEl('rk-footer-container', 'container', 'Footer Container', 'rk-footer-section', ['rk-footer-brand', 'rk-footer-links', 'rk-footer-support', 'rk-footer-social'], '', {
    maxWidth: '1200px', margin: '0 auto', padding: '0 24px 48px 24px', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '48px'
  });
  const footerBrand = makeEl('rk-footer-brand', 'div', 'Footer Brand', 'rk-footer-container', ['rk-footer-logo', 'rk-footer-tagline'], '', { display: 'block' });
  const footerLogo = makeEl('rk-footer-logo', 'heading', 'Footer Logo', 'rk-footer-brand', [], 'EventSphere', { fontSize: '24px', fontWeight: '800', color: '#C9A84C', marginBottom: '12px', display: 'block' });
  const footerTagline = makeEl('rk-footer-tagline', 'paragraph', 'Footer Tagline', 'rk-footer-brand', [], 'India\'s premier event discovery and ticketing platform. Connecting people through unforgettable experiences.', { fontSize: '14px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.7', display: 'block', margin: '0' });
  const footerLinks = makeEl('rk-footer-links', 'div', 'Footer Links', 'rk-footer-container', ['rk-footer-links-title', 'rk-footer-link1', 'rk-footer-link2', 'rk-footer-link3'], '', { display: 'block' });
  const footerLinksTitle = makeEl('rk-footer-links-title', 'heading', 'Links Title', 'rk-footer-links', [], 'Quick Links', { fontSize: '14px', fontWeight: '700', color: '#ffffff', marginBottom: '16px', display: 'block' });
  const fl1 = makeEl('rk-footer-link1', 'link', 'Footer Link 1', 'rk-footer-links', [], 'Browse Events', { color: 'rgba(255,255,255,0.5)', fontSize: '13px', textDecoration: 'none', display: 'block', marginBottom: '10px' }, { href: '#' });
  const fl2 = makeEl('rk-footer-link2', 'link', 'Footer Link 2', 'rk-footer-links', [], 'Create Event', { color: 'rgba(255,255,255,0.5)', fontSize: '13px', textDecoration: 'none', display: 'block', marginBottom: '10px' }, { href: '#' });
  const fl3 = makeEl('rk-footer-link3', 'link', 'Footer Link 3', 'rk-footer-links', [], 'About Us', { color: 'rgba(255,255,255,0.5)', fontSize: '13px', textDecoration: 'none', display: 'block', marginBottom: '10px' }, { href: '#' });
  const footerSupport = makeEl('rk-footer-support', 'div', 'Footer Support', 'rk-footer-container', ['rk-footer-support-title', 'rk-footer-sup1', 'rk-footer-sup2', 'rk-footer-sup3'], '', { display: 'block' });
  const footerSupportTitle = makeEl('rk-footer-support-title', 'heading', 'Support Title', 'rk-footer-support', [], 'Support', { fontSize: '14px', fontWeight: '700', color: '#ffffff', marginBottom: '16px', display: 'block' });
  const fs1 = makeEl('rk-footer-sup1', 'link', 'Support Link 1', 'rk-footer-support', [], 'Help Center', { color: 'rgba(255,255,255,0.5)', fontSize: '13px', textDecoration: 'none', display: 'block', marginBottom: '10px' }, { href: '#' });
  const fs2 = makeEl('rk-footer-sup2', 'link', 'Support Link 2', 'rk-footer-support', [], 'Contact Us', { color: 'rgba(255,255,255,0.5)', fontSize: '13px', textDecoration: 'none', display: 'block', marginBottom: '10px' }, { href: '#' });
  const fs3 = makeEl('rk-footer-sup3', 'link', 'Support Link 3', 'rk-footer-support', [], 'Privacy Policy', { color: 'rgba(255,255,255,0.5)', fontSize: '13px', textDecoration: 'none', display: 'block', marginBottom: '10px' }, { href: '#' });
  const footerSocial = makeEl('rk-footer-social', 'div', 'Footer Social', 'rk-footer-container', ['rk-footer-social-title', 'rk-footer-social-links'], '', { display: 'block' });
  const footerSocialTitle = makeEl('rk-footer-social-title', 'heading', 'Social Title', 'rk-footer-social', [], 'Follow Us', { fontSize: '14px', fontWeight: '700', color: '#ffffff', marginBottom: '16px', display: 'block' });
  const footerSocialLinks = makeEl('rk-footer-social-links', 'div', 'Social Links', 'rk-footer-social', ['rk-soc-fb', 'rk-soc-tw', 'rk-soc-ig'], '', { display: 'flex', gap: '12px' });
  const socFb = makeEl('rk-soc-fb', 'button', 'Facebook', 'rk-footer-social-links', [], 'f', { width: '36px', height: '36px', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.1)', color: '#ffffff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' });
  const socTw = makeEl('rk-soc-tw', 'button', 'Twitter', 'rk-footer-social-links', [], 'X', { width: '36px', height: '36px', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.1)', color: '#ffffff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' });
  const socIg = makeEl('rk-soc-ig', 'button', 'Instagram', 'rk-footer-social-links', [], '📷', { width: '36px', height: '36px', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.1)', color: '#ffffff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' });
  const footerBottom = makeEl('rk-footer-bottom', 'div', 'Footer Bottom', 'rk-footer-section', ['rk-footer-copy'], '', {
    borderTop: '1px solid rgba(255,255,255,0.08)', padding: '20px 24px', textAlign: 'center', display: 'block'
  });
  const footerCopy = makeEl('rk-footer-copy', 'text', 'Copyright', 'rk-footer-bottom', [], '© 2025 EventSphere · Rani Kamlapati. All rights reserved.', {
    fontSize: '13px', color: 'rgba(255,255,255,0.35)', display: 'block'
  });
  [footerSection, footerContainer, footerBrand, footerLogo, footerTagline, footerLinks, footerLinksTitle, fl1, fl2, fl3, footerSupport, footerSupportTitle, fs1, fs2, fs3, footerSocial, footerSocialTitle, footerSocialLinks, socFb, socTw, socIg, footerBottom, footerCopy].forEach(add);
  rootChildren.push('rk-footer-section');

  return {
    id: generatePageId(),
    name: 'Home',
    isHomepage: true,
    seo: { slug: '/', title: 'Rani Kamlapati – Home', metaDescription: 'Welcome to Rani Kamlapati official website', ogImage: '' },
    elements,
    rootChildren,
    cssClasses: {}
  };
}